import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'

// ── Async thunks → hit your Oracle pool API ──────────────────
export const loadRecord = createAsyncThunk(
  'forms/loadRecord',
  async ({ tabId, endpoint, id }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/${endpoint}/${id}`)
      return { tabId, data }
    } catch (e) {
      return rejectWithValue({ tabId, error: e.response?.data?.message || e.message })
    }
  }
)

export const saveRecord = createAsyncThunk(
  'forms/saveRecord',
  async ({ tabId, endpoint, formData }, { rejectWithValue }) => {
    try {
      const method = formData.id ? 'put' : 'post'
      const { data } = await axios[method](`/api/${endpoint}`, formData)
      return { tabId, data }
    } catch (e) {
      return rejectWithValue({ tabId, error: e.response?.data?.message || e.message })
    }
  }
)

const formsSlice = createSlice({
  name: 'forms',
  initialState: {},   // { [tabId]: { data, originalData, isDirty, status, errors } }
  reducers: {
    initForm: (state, { payload: { tabId, formType, defaults } }) => {
      state[tabId] = {
        formType,
        data: { ...defaults },
        originalData: { ...defaults },
        isDirty: false,
        status: 'idle',       // idle | loading | loaded | saving | saved | error
        errors: {},
        savedAt: null,
      }
    },
    setField: (state, { payload: { tabId, field, value } }) => {
      if (!state[tabId]) return
      state[tabId].data[field] = value
      state[tabId].isDirty = Object.keys(state[tabId].data)
        .some(k => state[tabId].data[k] !== state[tabId].originalData[k])
    },
    setFieldError: (state, { payload: { tabId, field, error } }) => {
      if (state[tabId]) state[tabId].errors[field] = error
    },
    clearFieldError: (state, { payload: { tabId, field } }) => {
      if (state[tabId]) delete state[tabId].errors[field]
    },
    discardChanges: (state, { payload: tabId }) => {
      if (!state[tabId]) return
      state[tabId].data    = { ...state[tabId].originalData }
      state[tabId].isDirty = false
      state[tabId].errors  = {}
    },
    removeForm: (state, { payload: tabId }) => { delete state[tabId] },
    resetAllForms: () => ({}),
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadRecord.pending, (s, a) => {
        if (s[a.meta.arg.tabId]) s[a.meta.arg.tabId].status = 'loading'
      })
      .addCase(loadRecord.fulfilled, (s, { payload: { tabId, data } }) => {
        if (!s[tabId]) return
        s[tabId].data         = { ...data }
        s[tabId].originalData = { ...data }
        s[tabId].isDirty      = false
        s[tabId].status       = 'loaded'
      })
      .addCase(loadRecord.rejected, (s, { payload: { tabId, error } }) => {
        if (s[tabId]) { s[tabId].status = 'error'; s[tabId].errors.load = error }
      })
      .addCase(saveRecord.pending, (s, a) => {
        if (s[a.meta.arg.tabId]) s[a.meta.arg.tabId].status = 'saving'
      })
      .addCase(saveRecord.fulfilled, (s, { payload: { tabId, data } }) => {
        if (!s[tabId]) return
        s[tabId].data         = { ...data }
        s[tabId].originalData = { ...data }
        s[tabId].isDirty      = false
        s[tabId].status       = 'saved'
        s[tabId].savedAt      = new Date().toISOString()
      })
      .addCase(saveRecord.rejected, (s, { payload: { tabId, error } }) => {
        if (s[tabId]) { s[tabId].status = 'error'; s[tabId].errors.save = error }
      })
  },
})

export const { initForm, setField, setFieldError, clearFieldError,
               discardChanges, removeForm, resetAllForms } = formsSlice.actions
export const selectForm   = (tabId) => (s) => s.forms[tabId]
export const selectField  = (tabId, field) => (s) => s.forms[tabId]?.data[field]
export default formsSlice.reducer